<script setup lang="ts">
import { useQuery, useQueryClient } from "@tanstack/vue-query";
import Center from "ls/layouts/Center.vue";
import { queries } from "ls/state/queries";
import { InfoIcon } from "lucide-vue-next";
import { computed, ref, watch } from "vue";
import { SimpleAlert, SimpleAlertDescription, SimpleAlertTitle } from "./simple-alert";
import SimpleButton from "./simple-button/SimpleButton.vue";
import Text from "./Text.vue";
import TextLinkEmailSupport from "./TextLinkEmailSupport.vue";

const { isFetched, isError, data } = useQuery(queries.config());
const showError = computed(() => isFetched.value && (!data.value || isError.value));

// just reload the page if the error has resolved
watch(showError, (hasError, hadError) => {
  if (!hasError && hadError) {
    window.location.reload();
  }
});

const isRetrying = ref(false);

const client = useQueryClient();
async function handleRetry() {
  try {
    isRetrying.value = true;
    await client.invalidateQueries(queries.config());
  } finally {
    isRetrying.value = false;
  }
}
</script>

<template>
  <Center v-if="showError">
    <div class="sm:max-w-xl">
      <div class="rounded-sm bg-white p-6 shadow-xs">
        <h1 class="mb-4 text-lg font-semibold">
          Failed to connect to Axure Cloud
        </h1>
        <SimpleAlert variant="destructive">
          <InfoIcon class="size-4" aria-hidden="true" />
          <SimpleAlertTitle>Error</SimpleAlertTitle>
          <SimpleAlertDescription>We encountered an issue connecting to Axure Cloud</SimpleAlertDescription>
        </SimpleAlert>
        <Text class="mt-4">
          This could be due to network issues, server problems, or an incorrect configuration. Please try again or contact support at <TextLinkEmailSupport /> if the problem persists.
        </Text>
        <div class="mt-4 text-center">
          <SimpleButton size="large" :disabled="isRetrying" @click="handleRetry">
            <div class="grid place-items-center">
              <span :class="{ invisible: !isRetrying }" class="col-1 row-1">Retrying</span>
              <span :class="{ invisible: isRetrying }" class="col-1 row-1">Retry</span>
            </div>
          </SimpleButton>
        </div>
      </div>
    </div>
  </Center>
</template>
